import SelectorIcon from "@assets/icons/selector.svg";
import CheckIcon from "@assets/icons/check.svg";

import React, { useMemo } from "react";
import type { OptionProps, SingleValueProps, StylesConfig } from "react-select";
import { components } from "react-select";

import { useDealSourceTypes } from "@components/AddNewEntity/hooks/useDealSourceTypes";
import { Select, ClearIndicator } from "@ui/Select";

export interface TypeOption {
  value: string;
  label: string;
  is_default?: boolean;
}

interface IDealSourceSelect {
  onChange: (value: TypeOption | null) => void;
  value: TypeOption | null;
  autoFocus?: boolean;
  placeholder?: string;
}

const getCustomStyles = (): StylesConfig<TypeOption, false> => ({
  clearIndicator: (provided) => ({
    ...provided,
    marginRight: "4px",
  }),
  container: (provided) => ({
    ...provided,
    width: "100%",
  }),
  control: (provided) => ({
    ...provided,
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
  }),
  menu: (provided) => ({
    ...provided,
    // Above the form's sticky footer (z-[1]), which would otherwise hide the menu.
    zIndex: 2,
    width: "100%",
    minWidth: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  }),
  option: (provided) => ({
    ...provided,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  singleValue: (provided) => ({
    ...provided,
    maxWidth: "85%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
  }),
});

const Option = (props: OptionProps<TypeOption, false>) => {
  const { isSelected, children } = props;
  return (
    <components.Option {...props}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {children}
        </span>
        {isSelected && <CheckIcon width={20} height={20} style={{ marginLeft: 8 }} />}
      </div>
    </components.Option>
  );
};

const SingleValue = (props: SingleValueProps<TypeOption, false>) => (
  <components.SingleValue {...props}>
    <span title={props.data.label}>{props.data.label}</span>
  </components.SingleValue>
);

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <SelectorIcon width={18} height={18} />
  </components.DropdownIndicator>
);

export const DealSourceSelect = ({
  onChange,
  value,
  autoFocus,
  placeholder = "Select type",
}: IDealSourceSelect) => {
  const { data } = useDealSourceTypes();

  const options = useMemo<TypeOption[]>(() => {
    if (!data?.results) return [];
    return data.results.map((item) => ({
      value: item.id,
      label: item.title,
      is_default: item.is_default,
    }));
  }, [data]);

  const selectedOption = options.find((opt) => opt.value === value?.value) || null;

  return (
    <Select<TypeOption, false>
      options={options}
      value={selectedOption}
      onChange={(opt) => onChange(opt ? opt : null)}
      isClearable
      isSearchable={false}
      autoFocus={autoFocus}
      placeholder={placeholder}
      styles={getCustomStyles()}
      components={{
        Option,
        SingleValue,
        DropdownIndicator,
        ClearIndicator,
      }}
      menuPosition="absolute"
    />
  );
};

export default DealSourceSelect;
